//
// Copyright (c) 2011-2023 by Curalate, Inc.
//

import { FeatureNames } from '@bvengineering/curalate-features';
import { createSelector } from 'reselect';

import { ICuralateStore } from '@models/AppState';
import { createHasFeatureSelector } from '@redux/selectors/createHasFeatureSelector';

const getInstagramAccount = (state: ICuralateStore) => state.instagram;

export const getUsername = createSelector(
  getInstagramAccount,
  (instagram) => instagram.account.defaultInstagramAccount?.username,
);

export const getInstagramEngagement = (state: ICuralateStore) =>
  state.instagram.engagement.posts;

export const getDateRange = createSelector(
  getInstagramEngagement,
  (posts) => posts.dateRange,
);

export const getEngagementPostFilter = createSelector(
  getInstagramEngagement,
  (posts) => posts.postType,
);

export const getFetchPostPerformanceExportError = createSelector(
  getInstagramEngagement,
  (posts) => posts.fetchPostPerformanceExportError,
);

export const getIsFetchingCommentsExport = createSelector(
  getInstagramEngagement,
  (posts) => posts.flags.isFetchingCommentsExport,
);

export const getIsFetchingExportPostPerformanceToEmail = createSelector(
  getInstagramEngagement,
  (posts) => posts.flags.isFetchingExportPostPerformanceToEmail,
);

export const getIsFetchingPostPerformanceExport = createSelector(
  getInstagramEngagement,
  (posts) => posts.flags.isFetchingPostPerformanceExport,
);

export const getInstagramEngagementPosts = createSelector(
  getInstagramEngagement,
  (posts) => posts.posts,
);

export const getIsFetchingInstagramEngagementPosts = createSelector(
  getInstagramEngagement,
  (posts) => posts.flags.isFetchingPosts,
);

export const getSelectedLabels = createSelector(
  getInstagramEngagement,
  (posts) => posts.selectedLabels,
);

export const getLabels = createSelector(
  getInstagramEngagement,
  (posts) => posts.labels,
);

export const getLimit = createSelector(
  getInstagramEngagement,
  (posts) => posts.limit,
);

export const getOffset = createSelector(
  getInstagramEngagement,
  (posts) => posts.offset,
);

export const getPostViewType = createSelector(
  getInstagramEngagement,
  (posts) => posts.postViewType,
);

export const getSortDir = createSelector(
  getInstagramEngagement,
  (posts) => posts.sortDir,
);

export const getSortKey = createSelector(
  getInstagramEngagement,
  (posts) => posts.sortKey,
);

export const getTotalPostCount = createSelector(
  getInstagramEngagement,
  (posts) => posts.totalPostCount,
);

export const getSelectedTrendGraphValue = createSelector(
  getInstagramEngagement,
  (posts) => posts.selectedTrendGraphValue,
);

export const getTrendStats = createSelector(
  getInstagramEngagement,
  (posts) => posts.trendStats,
);

export const getUserEmail = (state: ICuralateStore) => state.session.user.email;

export const getIsCsvDirectDownloadEnabled = createHasFeatureSelector(
  FeatureNames.InstagramEngagementCsvDirectDownload,
);
